import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Standalone shell for the SMS / 10DLC compliance pages.
 *
 * IMPORTANT — DO NOT alter the public contract of this shell without
 * coordinating with Twilio / The Campaign Registry review:
 *   • These pages MUST remain publicly accessible (no auth, no paywall).
 *   • Both URLs are submitted in the A2P 10DLC Campaign Registration
 *     ("Privacy Policy URL" and "Terms and Conditions URL").
 *   • TCR reviewers fetch these pages by URL — keep them lightweight,
 *     keep the canonical sections in place, and keep the brand /
 *     contact details accurate to Life Foundation.
 */
export default function SmsLegalShell({ title, eyebrow, lastUpdated, children, testId }) {
  // Lightweight page-title management so reviewers see the correct
  // document title in the browser tab.
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · Life Foundation`;
    return () => { document.title = prev; };
  }, [title]);

  return (
    <div
      data-testid={testId}
      className="min-h-screen bg-[#0b0f15] text-zinc-100 antialiased"
    >
      {/* Header */}
      <header className="border-b border-zinc-800/70 bg-[#0b0f15]/90 backdrop-blur">
        <div className="mx-auto max-w-3xl px-6 py-5 flex items-center justify-between">
          <Link
            to="/sms/privacy"
            data-testid="sms-legal-brand-link"
            className="text-sm font-semibold tracking-wide text-[#d4af37] hover:text-[#e5c45a] transition-colors"
          >
            THE LIFE FOUNDATION
          </Link>
          <nav className="text-xs text-zinc-400 space-x-5">
            <Link
              to="/sms/subscribe"
              data-testid="sms-legal-nav-subscribe"
              className="hover:text-zinc-100 transition-colors"
            >
              Subscribe
            </Link>
            <Link
              to="/sms/privacy"
              data-testid="sms-legal-nav-privacy"
              className="hover:text-zinc-100 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/sms/terms"
              data-testid="sms-legal-nav-terms"
              className="hover:text-zinc-100 transition-colors"
            >
              SMS Terms
            </Link>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-3xl px-6 py-10 sm:py-14">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#d4af37] mb-2">
            {eyebrow}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-3">
          {title}
        </h1>
        {lastUpdated && (
          <p className="text-sm text-zinc-400 mb-8">
            <span className="font-medium text-zinc-300">Last Updated:</span>{" "}
            {lastUpdated}
          </p>
        )}

        <article
          className="
            prose prose-invert max-w-none
            prose-headings:text-zinc-50 prose-headings:font-semibold
            prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3
            prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2
            prose-p:text-zinc-300 prose-p:leading-relaxed
            prose-li:text-zinc-300 prose-li:leading-relaxed
            prose-strong:text-zinc-50
            prose-a:text-[#d4af37] prose-a:no-underline hover:prose-a:underline
          "
        >
          {children}
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800/70 mt-10">
        <div className="mx-auto max-w-3xl px-6 py-8 text-xs text-zinc-500 space-y-2">
          <p>
            <strong className="text-zinc-300">Life Foundation</strong>{" "}
            · 7433 Springside Drive, Myrtle Beach, SC 29588 · EIN 99-3187855
          </p>
          <p>
            SMS program operated under the DBA <em>The Exchange Alliance</em>.
            For SMS support email{" "}
            <a
              href="mailto:will.smith@lifefoundationrebuild.com"
              className="text-[#d4af37] hover:underline"
              data-testid="sms-legal-support-email"
            >
              will.smith@lifefoundationrebuild.com
            </a>{" "}
            or call{" "}
            <a
              href="tel:+18434242655"
              className="text-[#d4af37] hover:underline"
              data-testid="sms-legal-support-phone"
            >
              +1 (843) 424-2655
            </a>.
          </p>
          <p className="pt-2">
            © {new Date().getFullYear()} Life Foundation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
